import { useEffect, useState } from 'react'
import { Mailbox, Loader2 } from 'lucide-react'
import api from '../../services/api'

/**
 * Where MY test email goes — on a test copy only, and only for the few team members named in
 * STAGING_MAIL_SWITCH_EMAILS. Each person decides for their own inbox.
 *
 * OFF (default)  the mail ProTrack sends you arrives in your own inbox.
 * ON             it is collected in the one test mailbox instead, with your name in the subject.
 *
 * Mail to anybody outside the team is always collected, whatever this says. On the live system
 * the endpoint answers { available: false } and nothing is shown.
 */
export default function StagingMailToggle({ isDark }) {
  const [s, setS] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    api.get('/admin/staging-mail').then(({ data }) => setS(data)).catch(() => setS({ available: false }))
  }, [])

  if (!s?.available || !s.may_change) return null

  const on = !!s.collecting
  const flip = async () => {
    if (busy || s.protected) return
    setBusy(true); setError(null)
    try {
      const { data } = await api.put('/admin/staging-mail', { collecting: !on })
      setS(data)
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not change where your test email goes.')
    } finally { setBusy(false) }
  }

  const textPrimary = isDark ? 'text-white' : 'text-gray-900'
  const textSecondary = isDark ? 'text-gray-400' : 'text-gray-500'

  return (
    <div className={`p-6 rounded-2xl border shadow-sm ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
      <div className="flex items-center gap-2 mb-4">
        <Mailbox className="w-5 h-5 text-teal-500" />
        <h2 className={`text-lg font-semibold ${textPrimary}`}>Where my test email goes</h2>
      </div>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className={`font-medium ${textPrimary}`}>
            {on ? 'Collected in the test mailbox' : 'Sent to me'}
          </p>
          <p className={`text-sm mt-0.5 ${textSecondary}`}>
            {on
              ? <>The mail ProTrack sends you here goes to {s.mailbox ? <span className="font-medium">{s.mailbox}</span> : 'the test mailbox'},
                  with your address in the subject. Turn this off to get it in your own inbox again.</>
              : <>The mail ProTrack sends you here arrives at <span className="font-medium">{s.email}</span>.
                  Turn this on to have it collected in the test mailbox instead.</>}
          </p>
          <p className={`text-xs mt-2 ${textSecondary}`}>
            Only your own mail changes. Mail for anybody outside the team is always collected in the test mailbox.
          </p>
        </div>
        <button type="button" role="switch" aria-checked={on} aria-label="Collect my test email in the test mailbox"
                onClick={flip} disabled={busy || s.protected}
                className={`relative shrink-0 inline-flex h-7 w-12 items-center rounded-full transition-colors
                  focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2
                  ${isDark ? 'focus:ring-offset-gray-800' : 'focus:ring-offset-white'}
                  ${on ? 'bg-teal-500' : (isDark ? 'bg-gray-600' : 'bg-gray-300')} ${s.protected ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}>
          <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${on ? 'translate-x-6' : 'translate-x-1'}`} />
          {busy && <Loader2 className="w-3.5 h-3.5 animate-spin absolute -right-6 text-teal-500" />}
        </button>
      </div>
      {s.protected && <p className={`mt-3 text-xs ${textSecondary}`}>Your address is protected on this copy, so your mail is always collected.</p>}
      {error && <p className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}
    </div>
  )
}
